/**
 * apps/web/src/lib/persistence.ts
 *
 * Offline-read persistence for the React Query cache. The dehydrated cache is
 * stored in IndexedDB (via idb-keyval — no localStorage 5 MB cap) so previously
 * loaded data (clients, projects, invoices, the Oggi feed) still renders when the
 * device is on weak or no signal.
 *
 * Tenant isolation is enforced by the `buster`: it embeds the user + workspace,
 * so when a different user (or workspace) is active, TanStack DISCARDS the
 * persisted cache instead of restoring it. This is a security control, not a
 * nicety — paired with purgePersistedCache() on logout for an immediate wipe.
 */

import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import type { AsyncStorage } from '@tanstack/query-persist-client-core';
import { get, set, del } from 'idb-keyval';

const CACHE_KEY = 'glarno-rq-cache';
const SCHEMA_VERSION = 'v1';

/** idb-keyval-backed AsyncStorage for the persister. */
const idbStorage: AsyncStorage<string> = {
  getItem: (key) => get<string>(key).then((value) => value ?? null),
  setItem: (key, value) => set(key, value),
  removeItem: (key) => del(key),
};

/** Create the cache persister. Storage + throttle are injectable for tests. */
export function createWebPersister(storage: AsyncStorage<string> = idbStorage, throttleTime = 1000) {
  return createAsyncStoragePersister({ storage, key: CACHE_KEY, throttleTime });
}

/**
 * The cache buster ties a persisted cache to ONE user + workspace + schema
 * version. A mismatch causes the persisted cache to be discarded on restore.
 */
export function sessionCacheBuster(userId: string | null, workspaceId: string | null): string {
  return `${SCHEMA_VERSION}:${userId ?? 'anon'}:${workspaceId ?? 'none'}`;
}

/** Immediately wipe the persisted cache (call on logout). */
export async function purgePersistedCache(): Promise<void> {
  await del(CACHE_KEY);
}
